import { useEffect } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { throttle } from 'lodash-es';

import { callService } from '@api/remote-call';
import {
  AddWishListItemRequestViewModel,
  CreateWishListViewModel,
  WishListUpdateRequestViewModel,
} from '@api/v3';
import { useAuthState } from '@contexts/auth/AuthContext';

// ===================================================
// This hook is used to add an item to a favorite list
// ===================================================
export const useAddFavoriteListItem = () => {
  const {
    mutate: addFavoriteListItem,
    isSuccess: isAddingFavoriteListItemSuccess,
    isError: isAddingFavoriteListItemError,
    error: isAddingFavoriteListItemErrorDetail,
    isLoading: isAddingFavoriteListItemLoading,
  } = useMutation(
    ['addFavoriteListItem'],
    ({
      wishListId,
      body,
      authorization,
    }: {
      wishListId: string;
      body?: AddWishListItemRequestViewModel;
      authorization: string;
    }) =>
      callService(
        {
          service: 'WishListsService',
          method: 'addWishList',
          version: 'v3',
        },
        { wishListId, body, authorization }
      )
  );

  return {
    addFavoriteListItem,
    isAddingFavoriteListItemSuccess,
    isAddingFavoriteListItemError,
    isAddingFavoriteListItemErrorDetail,
    isAddingFavoriteListItemLoading,
  };
};

// =======================================================
// =======================================================

// ===================================================
// This hook is used to add a new favorite list
// ===================================================
export const useAddNewFavoriteList = () => {
  const { loginInfo } = useAuthState();

  const {
    mutate: addNewFavoriteList,
    isSuccess: isAddingNewFavoriteListSuccess,
    isLoading: isAddingNewFavoriteListLoading,
    isError: isAddingNewFavoriteListError,
    error: isAddingNewFavoriteListErrorDetail,
  } = useMutation(['addNewFavoriteList'], (body: CreateWishListViewModel) =>
    callService(
      {
        service: 'WishListsService',
        method: 'createWishList',
        version: 'v3',
      },
      { authorization: loginInfo?.token, body }
    )
  );

  return {
    addNewFavoriteList,
    isAddingNewFavoriteListSuccess,
    isAddingNewFavoriteListLoading,
    isAddingNewFavoriteListError,
    isAddingNewFavoriteListErrorDetail,
  };
};

// ===================================================
// This hook is used to update a favorite list
// ===================================================
type UpdateBodyParams = {
  id: string;
  body?: WishListUpdateRequestViewModel;
};
export const useUpdateFavoriteList = () => {
  const {
    mutate: updateWishList,
    isSuccess: isUpdatingFavoriteListSuccess,
    isLoading: isUpdatingFavoriteListLoading,
    data: updatedList,
    isError: isUpdatingFavoriteListError,
    error: isUpdatingFavoriteListErrorDetail,
  } = useMutation(['updateWishList'], ({ id, body }: UpdateBodyParams) =>
    callService(
      {
        service: 'WishListsService',
        method: 'updateWishList',
        version: 'v3',
      },
      { id, body }
    )
  );

  return {
    updateWishList,
    isUpdatingFavoriteListSuccess,
    updatedList,
    isUpdatingFavoriteListLoading,
    isUpdatingFavoriteListError,
    isUpdatingFavoriteListErrorDetail,
  };
};

// =======================================================
// =======================================================

// ===================================================
// This hook is used to delete a favorite list
// ===================================================
export const useDeleteFavoriteList = () => {
  const {
    mutate: deleteFavoriteList,
    isSuccess: isDeletingFavoriteListSuccess,
    isLoading: isDeletingFavoriteListLoading,
  } = useMutation(['deleteFavoriteList'], (id: string) =>
    callService(
      {
        service: 'WishListsService',
        method: 'deleteWishList',
        version: 'v3',
      },
      { id }
    )
  );

  return {
    deleteFavoriteList,
    isDeletingFavoriteListSuccess,
    isDeletingFavoriteListLoading,
  };
};
// =======================================================
// =======================================================

// ===================================================
// This hook is used to delete a favorite list item
// ===================================================
export const useDeleteFavoriteListItem = () => {
  const {
    mutate: deleteFavoriteListItem,
    isSuccess: isDeletingFavoriteListItemSuccess,
    isLoading: isDeletingFavoriteListItemLoading,
  } = useMutation(
    ['deleteFavoriteListItem'],
    ({ wishListItemId }: { wishListItemId: string }) =>
      callService(
        {
          service: 'WishListsService',
          method: 'deleteWishListItem',
          version: 'v3',
        },
        { wishListItemId }
      )
  );

  return {
    deleteFavoriteListItem,
    isDeletingFavoriteListItemSuccess,
    isDeletingFavoriteListItemLoading,
  };
};

// =======================================================
// =======================================================

// ===================================================
// This hook is used to get selected wish list by item id
// ===================================================
export const useGetSelectedWishListsByItemId = (itemId, type, token) => {
  const {
    data: selectedItemAddedList,
    isError: isGettingSelectedWishListByItemIdIsError,
    isFetching: isGettingSelectedWishListByItemIdIsFetching,
    isLoading: isGettingSelectedWishListByItemIdIsLoading,
    refetch: refetchSelectedItemAddedList,
  } = useQuery({
    queryKey: ['getFavoriteListItems', itemId, token],
    queryFn: () =>
      callService(
        {
          service: 'WishListsService',
          method: 'getSelectedWishLists',
          version: 'v3',
        },
        { itemId: itemId, type: type, authorization: token }
      ),
    enabled: Boolean(token && itemId),
  });

  return {
    selectedItemAddedList: isGettingSelectedWishListByItemIdIsError
      ? []
      : selectedItemAddedList,
    isGettingSelectedWishListByItemIdIsError,
    isGettingSelectedWishListByItemIdIsFetching,
    isGettingSelectedWishListByItemIdIsLoading,
    refetchSelectedItemAddedList,
  };
};

// ===================================================
// This hook is used to delete item from all wish lists
// ===================================================
export const useDeleteItemFromAllWishLists = () => {
  const {
    mutate: deleteItemFromAllWishLists,
    isSuccess: isDeletingItemFromAllWishListsIsSuccess,
    isLoading: isDeletingItemFromAllWishListsIsLoading,
  } = useMutation(
    ['deleteItemFromAllWishLists'],
    ({
      itemId,
      type,
      authorization,
    }: {
      itemId: number;
      type: any;
      authorization: string;
    }) =>
      callService(
        {
          service: 'WishListsService',
          method: 'deleteSelectedWishLists',
          version: 'v3',
        },
        { itemId: itemId, type: type, authorization: authorization }
      )
  );

  return {
    deleteItemFromAllWishLists,
    isDeletingItemFromAllWishListsIsSuccess,
    isDeletingItemFromAllWishListsIsLoading,
  };
};

// =======================================================
// =======================================================

// ===================================================
// This hook is used to get all favorite lists
// ===================================================
type options = {
  token: string;
};
export const useGetFavoriteLists = ({ token }: options) => {
  const {
    data: favoriteLists,
    isError: isFavoriteListsError,
    isFetching: isFavoriteListsFetching,
    refetch: refetchFavoriteLists,
  } = useQuery({
    queryKey: ['getFavoriteLists'],
    queryFn: () =>
      callService(
        {
          service: 'WishListsService',
          method: 'getWishLists',
          version: 'v3',
        },
        { authorization: token }
      ),
    enabled: Boolean(token),
  });

  return {
    favoriteLists,
    isFavoriteListsError,
    isFavoriteListsFetching,
    refetchFavoriteLists,
  };
};
// =======================================================
// =======================================================

// ======================================================
// This hook is used to get all items of a favorite list
// ======================================================

type Options = {
  wishListId: string;
  page?: number;
  size?: number;
};

export const useGetFavoriteListItems = ({
  wishListId,
  page = 1,
  size = 9,
}: Options) => {
  const { loginInfo } = useAuthState();
  const token = loginInfo?.token;

  const {
    data: favoriteListItems,
    isError: isFavoriteListItemError,
    isFetching: isFavoriteListItemsFetching,
    isLoading: isFavoriteListItemsLoading,
    refetch: refetchFavoriteListItems,
  } = useQuery({
    queryKey: ['getFavoriteListItems', wishListId, page, size],
    queryFn: () =>
      callService(
        {
          service: 'WishListsService',
          method: 'getWishListItems',
          version: 'v3',
        },
        { wishListId, page, size }
      ),
    enabled: Boolean(token && wishListId),
  });

  return {
    favoriteListItems,
    refetchFavoriteListItems,
    isFavoriteListItemError,
    isFavoriteListItemsFetching,
    isFavoriteListItemsLoading,
  };
};

export const useGetFavoriteListItemsWithoutToken = ({
  wishListId,
  page = 1,
  size = 9,
}: Options) => {
  const {
    data: favoriteListItems,
    isError: isFavoriteListItemError,
    isFetching: isFavoriteListItemsFetching,
    isLoading: isFavoriteListItemsLoading,
    refetch: refetchFavoriteListItems,
  } = useQuery({
    queryKey: ['getFavoriteListItems', wishListId, page, size],
    queryFn: () =>
      callService(
        {
          service: 'WishListsService',
          method: 'getWishListItems',
          version: 'v3',
        },
        { wishListId, page, size }
      ),
    enabled: Boolean(wishListId),
  });

  return {
    favoriteListItems,
    refetchFavoriteListItems,
    isFavoriteListItemError,
    isFavoriteListItemsFetching,
    isFavoriteListItemsLoading,
  };
};
// =======================================================
// =======================================================

export const useOutsideClickWithoutTarget = (ref: any, callback: any): void => {
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback?.();
      }
    };
    document.addEventListener('mousedown', throttle(handleOutsideClick, 200));
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [callback, ref]);
};
